import {Component} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {CompanyService} from '../../../../../../@shared/services/company.service';
import {COR_COY_1} from '../../../../../../@shared/models/input.module';
import {CompanyCategoryResponse, CountryResponse} from '../../../../../../@shared/models/response.module';
import {StorageService} from '../../../../../../@core/services/storage.service';
import {ToastService} from '../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {EndpointService} from '../../../../../../@shared/endpoints.service';
import {AuthService} from '../../../../../../@core/services/auth.service';
import {StaticService} from '../../../../../../@shared/services/static-service';
import {PaymentService} from '../../../../../../@shared/services/payment.service';

@Component({
  selector: 'app-create-organisation',
  standalone: false,
  templateUrl: './create-organisation.html',
  styleUrl: './create-organisation.css'
})
export class CreateOrganisation {
  companyType: string = ''
  companyName: string = ''
  organisationNumber: string = ''
  address: string = ''
  city: string = ''
  postalCode: string = ''
  countryId: number | null = null
  phone: string = ''
  email: string = ''
  description: string = ''
  categoryId: number | null = null

  loading: boolean = false
  submitted: boolean = false

  companyTypes: { value: string; name: string; description: string; icon: string }[] = []
  categories: CompanyCategoryResponse[] = []
  countries: CountryResponse[] = []
  loadingCategories: boolean = false

  // Store registration fee
  readonly STORE_REGISTRATION_FEE = 299;

  constructor(
    public activeModal: NgbActiveModal,
    private companyService: CompanyService,
    private storageService: StorageService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private endpointService: EndpointService,
    private authService: AuthService,
    private staticService: StaticService,
    private paymentService: PaymentService
  ) {}

  ngOnInit() {
    this.initCompanyTypes();
    this.loadCountries();
    this.translateService.onLangChange.subscribe(() => {
      this.initCompanyTypes();
    });
  }

  private loadCountries() {
    this.staticService.getCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
      },
      error: (err) => {
        console.error('Failed to load countries', err);
      }
    });
  }

  private initCompanyTypes() {
    this.companyTypes = [
      {
        value: 'stable',
        name: this.translateService.instant('createOrganisation.typeStable'),
        description: this.translateService.instant('createOrganisation.typeStableDesc'),
        icon: 'fa-warehouse'
      },
      {
        value: 'riding_club',
        name: this.translateService.instant('createOrganisation.typeRidingClub'),
        description: this.translateService.instant('createOrganisation.typeRidingClubDesc'),
        icon: 'fa-users'
      },
      {
        value: 'riding_school',
        name: this.translateService.instant('createOrganisation.typeRidingSchool'),
        description: this.translateService.instant('createOrganisation.typeRidingSchoolDesc'),
        icon: 'fa-graduation-cap'
      },
      {
        value: 'stores_services',
        name: this.translateService.instant('createOrganisation.typeStoresServices'),
        description: this.translateService.instant('createOrganisation.typeStoresServicesDesc'),
        icon: 'fa-store'
      }
    ];
  }

  onCompanyTypeChange() {
    if (this.companyType === 'stores_services' && this.categories.length === 0) {
      this.loadCategories();
    }
    // Reset category when type changes
    if (this.companyType !== 'stores_services') {
      this.categoryId = null;
    }
  }

  private loadCategories() {
    this.loadingCategories = true;
    this.endpointService.cor_coy_32().subscribe({
      next: (res) => {
        this.categories = res.data || [];
        this.loadingCategories = false;
      },
      error: () => {
        this.categories = [];
        this.loadingCategories = false;
      }
    });
  }

  // Validation helpers
  get isCompanyTypeInvalid(): boolean {
    return this.submitted && !this.companyType;
  }

  get isCompanyNameInvalid(): boolean {
    return this.submitted && !this.companyName.trim();
  }

  get isAddressInvalid(): boolean {
    return this.submitted && !this.address.trim();
  }

  get isCityInvalid(): boolean {
    return this.submitted && !this.city.trim();
  }

  get isPostalCodeInvalid(): boolean {
    return this.submitted && !this.postalCode.trim();
  }

  get isCountryInvalid(): boolean {
    return this.submitted && !this.countryId;
  }

  get isEmailInvalid(): boolean {
    return this.submitted && !this.email.trim();
  }

  get isPhoneInvalid(): boolean {
    return this.submitted && !this.phone.trim();
  }

  get isCategoryInvalid(): boolean {
    return this.submitted && this.companyType === 'stores_services' && !this.categoryId;
  }

  get isFormValid(): boolean {
    const baseValid = !!(
      this.companyType &&
      this.companyName.trim() &&
      this.address.trim() &&
      this.city.trim() &&
      this.postalCode.trim() &&
      this.countryId &&
      this.email.trim() &&
      this.phone.trim()
    );

    // For stores_services, category is required
    if (this.companyType === 'stores_services') {
      return baseValid && !!this.categoryId;
    }

    return baseValid;
  }

  createOrganisation() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translateService.instant('createOrganisation.validationError'));
      return;
    }

    this.loading = true;

    // For stores_services, redirect to checkout (299 NOK registration fee)
    if (this.companyType === 'stores_services') {
      this.paymentService.createStoreCheckout({
        company_name: this.companyName.trim(),
        company_type: this.companyType,
        category_id: this.categoryId!,
        organisation_number: this.organisationNumber || undefined,
        address: this.address,
        city: this.city,
        postal_code: this.postalCode,
        country: '',
        country_id: this.countryId || undefined,
        phone: this.phone,
        email: this.email,
        description: this.description.trim() || undefined
      }).subscribe({
        next: (response) => {
          // Redirect to Stripe checkout
          window.location.href = response.checkout_url;
        },
        error: (error) => {
          this.loading = false;
          this.toastService.error(error.error?.reason || this.translateService.instant('createOrganisation.error'));
        }
      });
      return;
    }

    // For other company types, create directly (no payment required)
    const payload: COR_COY_1 = {
      company_name: this.companyName.trim(),
      company_type: this.companyType,
      category_id: this.companyType === 'stores_services' ? this.categoryId : undefined,
      organisation_number: this.organisationNumber,
      address: this.address,
      city: this.city,
      postal_code: this.postalCode,
      country: '',  // Legacy field - we now use country_id
      country_id: this.countryId,
      phone: this.phone,
      email: this.email,
      description: this.description.trim() || undefined,
      user_id: this.storageService.getUser().user_id
    }

    this.companyService.createCompany(payload).subscribe({
      next: (company) => {
        // Refresh the JWT token to include the new company in role_access
        this.authService.refreshToken().subscribe({
          next: () => {
            this.loading = false;
            this.toastService.success(this.translateService.instant('createOrganisation.success'));
            this.activeModal.close(company);
          },
          error: () => {
            // Even if token refresh fails, the company was created successfully
            this.loading = false;
            this.toastService.success(this.translateService.instant('createOrganisation.success'));
            this.activeModal.close(company);
          }
        });
      },
      error: (error) => {
        this.loading = false;
        this.toastService.error(error.error?.reason || this.translateService.instant('createOrganisation.error'));
      }
    });
  }

  close() {
    this.activeModal.dismiss()
  }
}
